import { Centrifuge, UnauthorizedError } from 'centrifuge';
import { refreshChatSessionTokens, clearChatSession } from './chatSessionTokens';

const TERMINAL_DISCONNECT_REASONS = new Set(['logged_out', 'offboarded']);
const SCOPE_CHANGE_DISCONNECT_REASONS = new Set(['chat_scope_changed']);

// Official Centrifuge SDK wrapper. Channels come from chatToken (server-side subs).
export default class ChatRealtimeClient {
  constructor() {
    this.client = null;
    this.onEvent = null;
    this.onStatus = null;
    this.pendingScopeRefresh = false;
    this.closing = false;
  }

  connect({ url, onEvent, onStatus }) {
    this.disconnect();
    this.closing = false;
    this.pendingScopeRefresh = false;
    this.onEvent = onEvent;
    this.onStatus = onStatus;

    this.client = new Centrifuge(url, {
      // Always remint via backend so Centrifugo JWT stays aligned with the session.
      getToken: () => this.resolveToken()
    });

    this.client.on('connecting', (ctx) => {
      this.emitStatus('connecting', ctx);
      // Server disconnect with reconnect=true (e.g. chat_scope_changed / 4001) does
      // NOT emit `disconnected`. It moves to Connecting and emits `connecting` with
      // the server code/reason. Without clearing the token, the SDK reuses the old
      // JWT (old channels) and never calls getToken → no /auth/refresh.
      if (this.closing) return;
      if (this.isScopeChange(ctx)) {
        this.pendingScopeRefresh = true;
        try {
          this.client.setToken('');
        } catch (e) {
          console.warn('[chat] failed to clear centrifugo token on scope change', e);
        }
      }
    });

    this.client.on('connected', () => {
      this.emitStatus('connected');
      if (this.pendingScopeRefresh) {
        this.pendingScopeRefresh = false;
        this.emitStatus('scope_refreshed');
      }
    });
    this.client.on('error', (ctx) => this.emitStatus('error', ctx));

    this.client.on('disconnected', (ctx) => {
      if (this.closing) return;

      const reason = ctx?.reason || '';
      const code = ctx?.code;
      this.emitStatus('server_disconnect', {
        reason,
        code,
        reconnect: ctx?.reconnect
      });

      if (TERMINAL_DISCONNECT_REASONS.has(reason) || code === 4501) {
        clearChatSession();
        this.closing = true;
        try {
          if (this.client) {
            this.client.disconnect();
          }
        } catch (e) {
          console.warn('[chat] centrifugo disconnect after terminal kick failed', e);
        }
        this.client = null;
        this.emitStatus('terminal_disconnect', { reason, code });
        return;
      }

      // Belt-and-suspenders: some paths may still surface scope change here.
      if (this.isScopeChange(ctx)) {
        this.pendingScopeRefresh = true;
        try {
          if (this.client) {
            this.client.setToken('');
          }
        } catch (e) {
          console.warn('[chat] failed to clear centrifugo token on scope change', e);
        }
      }
    });

    this.client.on('publication', (ctx) => {
      if (typeof this.onEvent !== 'function') return;
      this.onEvent({
        channel: ctx.channel,
        data: ctx.data
      });
    });

    this.client.connect();
  }

  isScopeChange(ctx) {
    const reason = ctx?.reason || '';
    const code = ctx?.code;
    return SCOPE_CHANGE_DISCONNECT_REASONS.has(reason) || code === 4001;
  }

  disconnect() {
    if (!this.client) return;
    this.closing = true;
    try {
      this.client.disconnect();
    } catch (e) {
      console.warn('[chat] centrifugo disconnect failed', e);
    }
    this.client = null;
    this.emitStatus('disconnected');
  }

  async resolveToken() {
    try {
      return await refreshChatSessionTokens();
    } catch (err) {
      this.emitStatus('refresh_failed', err);
      throw new UnauthorizedError('chat token unavailable');
    }
  }

  emitStatus(status, detail) {
    if (typeof this.onStatus === 'function') {
      this.onStatus(status, detail);
    }
  }
}
